import {
  Dispatch,
  SetStateAction,
  useRef,
  useState,
  MouseEvent,
  ReactNode,
} from "react";
import { Button } from "reactstrap";
import classNames from "classnames";
import { useSetAtom } from "jotai";

import { useUserRecordActivityUpdate } from "../Records/References/ReferencesComponents/SelectableReferences/useUpdateUserRecordActivity";
import ReferenceTooltip from "../Records/References/ReferenceTooltip";
import ViewRecordDrawer from "../Drawers/Records/ViewRecordDrawer";
import { callFunctionIfExists } from "../../utils/GeneralUtils";
import SubitemDrawer from "../Drawers/Subitems/SubitemDrawer";
import { recordViewAtom } from "../../atoms/GlobalAtoms";
import { Field } from "../../types/TenantInterfaces";
import AppSpinner from "../../elements/AppSpinner";
import IconButton from "../../elements/IconButton";
import AppTooltip from "../../elements/AppTooltip";
import useModule from "../../hooks/useModule";
import useHover from "../../hooks/useHover";

interface SelectableRowProps {
  item: any;
  handleSelectItem: (item: any) => void;
  isItemSelected: boolean;
  displayName: string;
  isSaving?: boolean;
  onOpen: () => void;
  onClose: () => void;
  disabled?: boolean;
  showDisplayButton?: boolean;
  description: ReactNode | string | null;
  setKeepOpen: Dispatch<SetStateAction<boolean>>;
  imageField: string | null;
  imageFieldPresentation: string | null;
  selectable?: boolean;
  showArrowButton?: boolean;
  subitemField: Field | undefined;
  subitemRecord: any | undefined;
  index: number | null;
  isFavorite: boolean;
}

const SelectableRow = ({
  item,
  handleSelectItem,
  isItemSelected,
  displayName,
  isSaving,
  onOpen,
  onClose,
  disabled,
  showDisplayButton = true,
  description = null,
  setKeepOpen,
  imageField,
  imageFieldPresentation,
  selectable = true,
  showArrowButton = false,
  subitemField = undefined,
  subitemRecord = undefined,
  index = null,
  isFavorite = false,
}: SelectableRowProps) => {
  const { updateUserRecordActivityCache } = useUserRecordActivityUpdate();
  const [openRecordData, setOpenRecordData] = useState(false);
  const { module } = useModule(item?._ModuleAPIName || item?.ModuleAPIName);
  const isPublicForm = window.location.pathname.startsWith("/form");
  const [viewRecordID, setViewRecordID] = useState<string | null>(null);
  const setView = useSetAtom(recordViewAtom);
  const viewClickedRef = useRef(false);
  showDisplayButton = isPublicForm ? false : showDisplayButton;
  const [hoverRef, isHovered] = useHover<HTMLDivElement>();

  function handleFavorite(id: string) {
    if (!module || !id) return;

    updateUserRecordActivityCache({
      userRecordActivities: [
        {
          moduleAPIName: module.APIName,
          recordIDs: [id],
          type: "Favorites",
        },
      ],
    });
  }

  return (
    <>
      <div
        ref={hoverRef}
        className="tw:flex tw:flex-row tw:items-center tw:cursor-pointer tw:px-2 tw:py-1"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSelectItem(item);
        }}
      >
        {selectable && (
          <Button
            className="ml-1 btn btn-icon-only p-0 border-0"
            style={{ height: "24px", width: "24px", fontSize: "12px" }}
            color={isItemSelected ? "primary" : "superlight"}
            disabled={disabled || isSaving}
          >
            {isSaving ? (
              <AppSpinner size="sm" color="white" />
            ) : (
              <i
                className="mdi mdi-check p-0"
                style={{
                  fontSize: 22,
                  lineHeight: 1,
                  position: "relative",
                  top: 1,
                }}
              ></i>
            )}
          </Button>
        )}
        <AppTooltip
          onOpen={setKeepOpen ? () => setKeepOpen(true) : undefined}
          onClose={
            setKeepOpen
              ? () => {
                  if (viewClickedRef.current === true) {
                    viewClickedRef.current = false;
                  } else {
                    setKeepOpen(false);
                  }
                }
              : undefined
          }
          content={(ref: any) => (
            <ReferenceTooltip
              record={item}
              //@ts-ignore
              onView={() => {
                viewClickedRef.current = true;
                ref?.current?.close();
                onViewRecord();
              }}
              imageField={imageField}
              imageFieldPresentation={imageFieldPresentation}
              description={description}
            />
          )}
        >
          <div className="tw:flex tw:flex-col tw:overflow-hidden tw:flex-grow">
            <h4
              className={classNames("tw:m-0 tw:font-medium tw:truncate", {
                "tw:ml-2": !selectable,
              })}
              style={{ maxWidth: "200px", lineHeight: 1.3 }}
            >
              {displayName}
            </h4>
            {description && (
              <div
                className="tw:truncate"
                style={{ maxWidth: "220px", fontSize: 11, lineHeight: 1.3 }}
              >
                {description}
              </div>
            )}
          </div>
        </AppTooltip>
        <div className="tw:flex tw:items-center tw:ml-auto tw:space-x-1">
          {showDisplayButton && (
            <>
              {(isHovered || isFavorite) && (
                <IconButton
                  icon={isFavorite ? "mdi mdi-star" : "mdi mdi-star-outline"}
                  color="warning"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleFavorite(item?.id);
                  }}
                  size="sm"
                  className="tw:px-0"
                />
              )}
              <IconButton
                icon="mdi mdi-eye-outline"
                color="secondary"
                size="sm"
                outline={true}
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onViewRecord();
                }}
              />
            </>
          )}
          {showArrowButton && (
            <IconButton
              icon="mdi mdi-chevron-right"
              size="sm"
              color="secondary"
              outline
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                e.stopPropagation();
                handleSelectItem(item);
              }}
            />
          )}
        </div>
      </div>
      {openRecordData && !subitemField && (
        <ViewRecordDrawer
          open={!!viewRecordID}
          module={module!}
          recordID={viewRecordID ?? ""}
          onClose={onCloseRecord}
          onExited={() => setOpenRecordData(false)}
        />
      )}
      {openRecordData && subitemField && (
        <SubitemDrawer
          open={!!viewRecordID}
          existingRecord={subitemRecord}
          index={index}
          field={subitemField}
          onClose={onCloseRecord}
          onExited={() => setOpenRecordData(false)}
          displayMode={"SummaryCard"}
          moduleAPIName={module?.APIName}
        />
      )}
    </>
  );

  function onViewRecord() {
    setOpenRecordData(true);
    setView(true);
    setViewRecordID(item.id);
    callFunctionIfExists(onOpen);
  }

  function onCloseRecord() {
    setView(false);
    setViewRecordID(null);
    callFunctionIfExists(onClose);
    setKeepOpen(false);
  }
};

export default SelectableRow;
